import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  ExternalLink,
  PanelsTopLeft,
  RefreshCw,
  X,
  ImagePlus,
  Upload,
  Layers3,
  Code2,
  ListChecks,
  ChartNoAxesCombined,
  MessageSquareQuote,
  Globe,
  Building2,
  CalendarDays,
  Clock3,
  FileText,
  ChevronDown,
  Check,
  AlertCircle,
  Eye,
  ArrowUpRight,
} from "lucide-react";

import Modal from "../components/Modal";
import { PageHeader } from "../components/ui";
import {
  EmptyState,
  ErrorMessage,
  Loading,
  SuccessMessage,
} from "../components/Feedback";
import {
  api,
  endpoint,
  getErrorMessage,
  unwrapList,
} from "../services/api";

/* =========================================================
   CONSTANTS
========================================================= */

const createBlankForm = () => ({
  title: "",
  type: "website",
  description: "",
  fullDescription: "",
  websiteLink: "",
  client: "",
  category: "",
  year: String(new Date().getFullYear()),
  duration: "",
  technologies: [],
  features: [],
  results: [{ key: "", value: "" }],
  testimonial: {
    name: "",
    position: "",
    text: "",
  },
});

const FILE_FIELDS = [
  {
    name: "image",
    label: "Main project image",
    description: "Primary image displayed on the portfolio card.",
    required: true,
  },
  {
    name: "icon",
    label: "Project icon / logo",
    description: "Optional project or client logo.",
    required: false,
  },
  {
    name: "BackgroundImage",
    label: "Background image",
    description: "Optional background image for the project.",
    required: false,
  },
];

const inputClass = "input";
const labelClass = "label";
const sectionClass =
  "rounded-xl border border-slate-200 bg-white p-4 sm:p-6";

/* =========================================================
   HELPERS
========================================================= */

function imageUrl(value) {
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    return value.replace(
      /^https:\/\/localhost:4480(?=\/)/i,
      "https://pmswebbackend.pixelmindsolutions.com"
    );
  }

  return endpoint(value.startsWith("/") ? value : `/${value}`);
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function normalizeResults(value) {
  if (!value) return [{ key: "", value: "" }];

  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      value = {};
    }
  }

  if (value instanceof Map) {
    value = Object.fromEntries(value);
  }

  if (typeof value !== "object" || Array.isArray(value)) {
    return [{ key: "", value: "" }];
  }

  const entries = Object.entries(value).map(([key, val]) => ({
    key,
    value: String(val ?? ""),
  }));

  return entries.length ? entries : [{ key: "", value: "" }];
}

function normalizeTestimonial(value) {
  if (!value) {
    return { name: "", position: "", text: "" };
  }

  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      value = {};
    }
  }

  return {
    name: value?.name || "",
    position: value?.position || "",
    text: value?.text || "",
  };
}

function resultsToObject(rows) {
  return rows.reduce((result, row) => {
    const key = row.key.trim();
    if (key) {
      result[key] = row.value.trim();
    }
    return result;
  }, {});
}

/* =========================================================
   MEMOIZED PRESENTATIONAL COMPONENTS
========================================================= */

const Field = memo(function Field({
  label,
  hint,
  required,
  children,
  className = "",
}) {
  return (
    <div className={className}>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {hint && (
        <p className="mt-1.5 text-xs leading-5 text-slate-500">{hint}</p>
      )}
    </div>
  );
});

const SectionHeading = memo(function SectionHeading({
  icon: Icon,
  title,
  description,
  number,
}) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
        <Icon size={19} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {number && (
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
              {number}
            </span>
          )}
        </div>

        {description && (
          <p className="mt-1 text-sm leading-5 text-slate-500">
            {description}
          </p>
        )}
      </div>
    </div>
  );
});

/* =========================================================
   ARRAY INPUT (memoized, stable onChange from parent)
========================================================= */

const ArrayInput = memo(function ArrayInput({
  label,
  description,
  icon: Icon,
  values,
  onChange,
  placeholder,
}) {
  const [draft, setDraft] = useState("");
  const [duplicateError, setDuplicateError] = useState("");

  const addItem = useCallback(() => {
    const value = draft.trim();
    if (!value) return;

    const duplicate = values.some(
      (item) => item.toLowerCase() === value.toLowerCase()
    );

    if (duplicate) {
      setDuplicateError("This item already exists.");
      return;
    }

    onChange([...values, value]);
    setDraft("");
    setDuplicateError("");
  }, [draft, values, onChange]);

  const removeItem = useCallback(
    (index) => {
      onChange(values.filter((_, i) => i !== index));
      setDuplicateError("");
    },
    [values, onChange]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        addItem();
      }
    },
    [addItem]
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
      <div className="mb-3 flex items-start gap-2">
        {Icon && (
          <Icon size={17} className="mt-0.5 shrink-0 text-brand-700" />
        )}

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">{label}</p>
          {description && (
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {description}
            </p>
          )}
        </div>

        <span className="rounded-lg bg-white px-2 py-1 text-xs font-semibold text-slate-500">
          {values.length}
        </span>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          className={inputClass}
          value={draft}
          placeholder={placeholder}
          onChange={(event) => {
            setDraft(event.target.value);
            setDuplicateError("");
          }}
          onKeyDown={handleKeyDown}
        />

        <button
          type="button"
          onClick={addItem}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700"
        >
          <Plus size={16} />
          Add
        </button>
      </div>

      {duplicateError && (
        <p className="mt-2 text-xs text-red-600">{duplicateError}</p>
      )}

      <div className="mt-3 flex min-h-8 flex-wrap gap-2">
        {values.map((value, index) => (
          <span
            key={`${value}-${index}`}
            className="inline-flex max-w-full items-center gap-2 rounded-lg border border-brand-100 bg-white px-2.5 py-1.5 text-sm text-slate-700 shadow-sm"
          >
            <span className="break-all">{value}</span>
            <button
              type="button"
              onClick={() => removeItem(index)}
              aria-label={`Remove ${value}`}
              className="shrink-0 rounded-md p-0.5 text-slate-500 transition hover:bg-red-50 hover:text-red-600"
            >
              <X size={14} />
            </button>
          </span>
        ))}

        {values.length === 0 && (
          <p className="py-1 text-xs text-slate-500">No items added yet.</p>
        )}
      </div>
    </div>
  );
});

/* =========================================================
   RESULTS EDITOR (memoized)
========================================================= */

const ResultsEditor = memo(function ResultsEditor({ rows, onChange }) {
  const updateRow = useCallback(
    (index, field, value) => {
      onChange(
        rows.map((row, i) =>
          i === index ? { ...row, [field]: value } : row
        )
      );
    },
    [rows, onChange]
  );

  const addRow = useCallback(() => {
    onChange([...rows, { key: "", value: "" }]);
  }, [rows, onChange]);

  const removeRow = useCallback(
    (index) => {
      onChange(rows.filter((_, i) => i !== index));
    },
    [rows, onChange]
  );

  const preview = useMemo(() => JSON.stringify(resultsToObject(rows)), [rows]);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">
            Project results
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add metrics such as traffic, conversion, or speed.
          </p>
        </div>

        <button
          type="button"
          onClick={addRow}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-xs font-semibold text-brand-700 transition hover:bg-brand-100"
        >
          <Plus size={14} />
          Add result
        </button>
      </div>

      <div className="space-y-3">
        {rows.map((row, index) => (
          <div
            key={index}
            className="grid grid-cols-1 items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/70 p-3 sm:grid-cols-[1fr_1fr_auto]"
          >
            <input
              className={inputClass}
              value={row.key}
              placeholder="Metric (e.g. Traffic)"
              onChange={(event) =>
                updateRow(index, "key", event.target.value)
              }
              aria-label="Result name"
            />

            <input
              className={inputClass}
              value={row.value}
              placeholder="Value (e.g. +150%)"
              onChange={(event) =>
                updateRow(index, "value", event.target.value)
              }
              aria-label="Result value"
            />

            <button
              type="button"
              onClick={() => removeRow(index)}
              aria-label="Remove result"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-lg bg-brand-50 p-3 text-xs leading-5 text-brand-700">
        <span className="font-semibold">Preview:</span> {preview}
      </div>
    </div>
  );
});

/* =========================================================
   IMAGE UPLOAD (memoized)
========================================================= */

const ImageUploadField = memo(function ImageUploadField({
  name,
  label,
  description,
  required,
  selectedFile,
  existingImage,
  onChange,
}) {
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!selectedFile) {
      setPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const currentPreview = preview || imageUrl(existingImage);

  const handleFile = useCallback(
    (event) => {
      onChange(event.target.files?.[0] || null);
      event.target.value = "";
    },
    [onChange]
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          <ImagePlus size={17} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            {description}
          </p>
        </div>
      </div>

      <div className="mb-3 flex h-36 items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-200 bg-slate-50">
        {currentPreview ? (
          <img
            src={currentPreview}
            alt={`${label} preview`}
            className="h-full w-full object-contain"
            onError={(event) => {
              event.currentTarget.style.opacity = "0";
            }}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-500">
            <Upload size={23} />
            <span className="text-xs">Image preview</span>
          </div>
        )}
      </div>

      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800">
        <Upload size={15} />
        {selectedFile ? "Change image" : "Choose image"}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="sr-only"
          onChange={handleFile}
        />
      </label>

      {selectedFile && (
        <div className="mt-2 flex items-start justify-between gap-2">
          <p className="min-w-0 break-all text-xs text-slate-500">
            {selectedFile.name}
          </p>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="shrink-0 text-xs font-semibold text-red-600 hover:text-red-700"
          >
            Remove
          </button>
        </div>
      )}

      {!selectedFile && existingImage && (
        <p className="mt-2 break-all text-xs text-slate-500">
          Existing image retained unless replaced.
        </p>
      )}
    </div>
  );
});

/* =========================================================
   GALLERY UPLOAD (memoized)
========================================================= */

const GalleryUpload = memo(function GalleryUpload({
  gallery,
  setGallery,
  existingGallery = [],
}) {
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const urls = gallery.map((file) => URL.createObjectURL(file));
    setPreviews(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [gallery]);

  const handleFiles = useCallback(
    (event) => {
      const selected = Array.from(event.target.files || []);
      const images = selected.filter((file) =>
        file.type.startsWith("image/")
      );

      if (images.length > 7) {
        window.alert("You can select a maximum of 7 gallery images.");
      }

      setGallery(images.slice(0, 7));
      event.target.value = "";
    },
    [setGallery]
  );

  const removeAt = useCallback(
    (index) => {
      setGallery((previous) =>
        previous.filter((_, i) => i !== index)
      );
    },
    [setGallery]
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">
            Gallery images
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Select up to 7 images. Uploading a new gallery replaces the
            existing gallery.
          </p>
        </div>

        <label className="inline-flex cursor-pointer shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-brand-300 hover:text-brand-800">
          <ImagePlus size={16} />
          Select images
          <input
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={handleFiles}
          />
        </label>
      </div>

      {gallery.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {gallery.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="relative overflow-hidden rounded-lg border border-slate-200 bg-white"
            >
              <img
                src={previews[index]}
                alt={file.name}
                className="h-24 w-full object-cover"
              />
              <p className="truncate px-2 py-2 text-xs text-slate-600">
                {file.name}
              </p>
              <button
                type="button"
                onClick={() => removeAt(index)}
                aria-label={`Remove ${file.name}`}
                className="absolute right-1.5 top-1.5 rounded-lg bg-white/95 p-1.5 text-red-600 shadow hover:bg-red-50"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : existingGallery.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {existingGallery.map((src, index) => (
            <div
              key={`${src}-${index}`}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white"
            >
              <img
                src={imageUrl(src)}
                alt={`Existing gallery ${index + 1}`}
                className="h-24 w-full object-cover"
              />
              <p className="px-2 py-2 text-xs text-slate-500">
                Existing image {index + 1}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white py-8 text-slate-500">
          <ImagePlus size={25} />
          <p className="mt-2 text-sm font-medium">No gallery images</p>
          <p className="mt-1 text-xs">Selected images will appear here.</p>
        </div>
      )}

      <p className="mt-3 text-xs text-slate-500">
        {gallery.length > 0
          ? `${gallery.length} new image(s) selected`
          : `${existingGallery.length} existing image(s)`}
      </p>
    </div>
  );
});

/* =========================================================
   VIEW HELPERS (memoized)
========================================================= */

const DetailItem = memo(function DetailItem({ label, value }) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 break-words text-sm font-medium text-slate-800">
        {value}
      </p>
    </div>
  );
});

const DetailSection = memo(function DetailSection({
  title,
  icon: Icon,
  children,
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="mb-4 flex items-center gap-2">
        <div className="rounded-lg bg-brand-50 p-2 text-brand-700">
          <Icon size={17} />
        </div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </section>
  );
});

const PortfolioView = memo(function PortfolioView({ project }) {
  const results =
    project.results instanceof Map
      ? Object.fromEntries(project.results)
      : project.results || {};

  const testimonial = project.testimonial || {};

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="relative h-52 bg-slate-100 sm:h-72">
          {project.image ? (
            <img
              src={imageUrl(project.image)}
              alt={project.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-500">
              <PanelsTopLeft size={40} />
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
            <span className="inline-flex rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold capitalize text-white">
              {(project.type || "other").replace("-", " ")}
            </span>

            <h2 className="mt-3 text-2xl font-semibold text-white sm:text-3xl">
              {project.title}
            </h2>

            {project.client && (
              <p className="mt-1 text-sm text-white/80">
                Client: {project.client}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
          <DetailItem label="Category" value={project.category} />
          <DetailItem label="Year" value={project.year} />
          <DetailItem label="Duration" value={project.duration} />
          <DetailItem label="Client" value={project.client} />
        </div>

        {project.websiteLink && (
          <div className="border-t border-slate-100 px-5 py-4">
            <a
              href={project.websiteLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 break-all text-sm font-semibold text-brand-700 hover:text-brand-800"
            >
              <Globe size={16} />
              {project.websiteLink}
              <ArrowUpRight size={15} />
            </a>
          </div>
        )}
      </div>

      <DetailSection title="Project overview" icon={FileText}>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-700">
              Short description
            </h4>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-slate-600">
              {project.description || "No short description provided."}
            </p>
          </div>

          {project.fullDescription && (
            <div className="border-t border-slate-100 pt-4">
              <h4 className="text-sm font-semibold text-slate-700">
                Full description
              </h4>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-slate-600">
                {project.fullDescription}
              </p>
            </div>
          )}
        </div>
      </DetailSection>

      <DetailSection title="Technologies used" icon={Code2}>
        {project.technologies?.length ? (
          <div className="flex flex-wrap gap-2">
            {project.technologies.map((technology, index) => (
              <span
                key={`${technology}-${index}`}
                className="rounded-lg border border-brand-100 bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700"
              >
                {technology}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No technologies added.</p>
        )}
      </DetailSection>

      <DetailSection title="Project features" icon={ListChecks}>
        {project.features?.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {project.features.map((feature, index) => (
              <div
                key={`${feature}-${index}`}
                className="flex items-start gap-2 rounded-lg bg-slate-50 p-3"
              >
                <Check
                  size={16}
                  className="mt-0.5 shrink-0 text-emerald-600"
                />
                <span className="text-sm leading-5 text-slate-700">
                  {feature}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No features added.</p>
        )}
      </DetailSection>

      <DetailSection title="Project results" icon={ChartNoAxesCombined}>
        {Object.keys(results).length ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Object.entries(results).map(([key, value]) => (
              <div
                key={key}
                className="rounded-lg border border-slate-200 bg-slate-50 p-4"
              >
                <p className="text-xs font-medium text-slate-500">{key}</p>
                <p className="mt-2 break-words text-xl font-semibold text-slate-900">
                  {String(value)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No results added.</p>
        )}
      </DetailSection>

      {(testimonial.name ||
        testimonial.position ||
        testimonial.text) && (
        <DetailSection title="Client testimonial" icon={MessageSquareQuote}>
          <div className="rounded-lg bg-brand-50/70 p-5">
            {testimonial.text && (
              <p className="whitespace-pre-wrap text-sm italic leading-7 text-slate-700">
                “{testimonial.text}”
              </p>
            )}

            <div className="mt-4 border-t border-brand-100 pt-4">
              <p className="font-semibold text-slate-900">
                {testimonial.name || "Client"}
              </p>
              {testimonial.position && (
                <p className="mt-1 text-sm text-slate-500">
                  {testimonial.position}
                </p>
              )}
            </div>
          </div>
        </DetailSection>
      )}

      {(project.icon || project.BackgroundImage) && (
        <DetailSection title="Additional images" icon={ImagePlus}>
          <div className="grid gap-4 sm:grid-cols-2">
            {project.icon && (
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-700">
                  Project icon / logo
                </p>
                <div className="flex h-36 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <img
                    src={imageUrl(project.icon)}
                    alt={`${project.title} logo`}
                    className="h-full max-w-full object-contain"
                  />
                </div>
              </div>
            )}

            {project.BackgroundImage && (
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-700">
                  Background image
                </p>
                <img
                  src={imageUrl(project.BackgroundImage)}
                  alt={`${project.title} background`}
                  className="h-36 w-full rounded-lg border border-slate-200 object-cover"
                />
              </div>
            )}
          </div>
        </DetailSection>
      )}

      {project.gallery?.length > 0 && (
        <DetailSection title="Project gallery" icon={Layers3}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {project.gallery.map((image, index) => (
              <a
                key={`${image}-${index}`}
                href={imageUrl(image)}
                target="_blank"
                rel="noreferrer"
                className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
              >
                <img
                  src={imageUrl(image)}
                  alt={`${project.title} gallery ${index + 1}`}
                  className="h-36 w-full object-cover transition duration-300 group-hover:scale-105 sm:h-44"
                />
                <span className="absolute bottom-2 right-2 rounded-lg bg-white/95 p-2 text-slate-700 shadow">
                  <ExternalLink size={14} />
                </span>
              </a>
            ))}
          </div>
        </DetailSection>
      )}

      <div className="grid gap-3 rounded-lg bg-slate-50 p-4 text-xs text-slate-500 sm:grid-cols-2">
        <p>
          <span className="font-semibold text-slate-700">Created:</span>{" "}
          {project.createdAt
            ? new Date(project.createdAt).toLocaleString()
            : "—"}
        </p>
        <p>
          <span className="font-semibold text-slate-700">
            Last updated:
          </span>{" "}
          {project.updatedAt
            ? new Date(project.updatedAt).toLocaleString()
            : "—"}
        </p>
        <p className="break-all sm:col-span-2">
          <span className="font-semibold text-slate-700">Project ID:</span>{" "}
          {project._id}
        </p>
      </div>
    </div>
  );
});

/* =========================================================
   PORTFOLIO FORM (separate component → typing only re-renders
   the form, not the entire page / list / other modals)
========================================================= */

const PortfolioForm = memo(function PortfolioForm({
  editing,
  saving,
  onCancel,
  onSubmit,
}) {
  // Form state lives inside this component.
  const [form, setForm] = useState(() => {
    if (editing) {
      return {
        ...createBlankForm(),
        ...editing,
        title: editing.title || "",
        type: editing.type || "website",
        description: editing.description || "",
        fullDescription: editing.fullDescription || "",
        websiteLink: editing.websiteLink || "",
        client: editing.client || "",
        category: editing.category || "",
        year: editing.year ? String(editing.year) : "",
        duration: editing.duration || "",
        technologies: normalizeStringArray(editing.technologies),
        features: normalizeStringArray(editing.features),
        results: normalizeResults(editing.results),
        testimonial: normalizeTestimonial(editing.testimonial),
      };
    }
    return createBlankForm();
  });

  const [files, setFiles] = useState({});
  const [gallery, setGallery] = useState([]);

  // Stable per-field handler — no new closure per render.
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((previous) =>
      previous[name] === value ? previous : { ...previous, [name]: value }
    );
  }, []);

  const handleTestimonialChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((previous) => {
      if (previous.testimonial[name] === value) return previous;
      return {
        ...previous,
        testimonial: { ...previous.testimonial, [name]: value },
      };
    });
  }, []);

  const setTechnologies = useCallback((values) => {
    setForm((previous) =>
      previous.technologies === values
        ? previous
        : { ...previous, technologies: values }
    );
  }, []);

  const setFeatures = useCallback((values) => {
    setForm((previous) =>
      previous.features === values ? previous : { ...previous, features: values }
    );
  }, []);

  const setResults = useCallback((results) => {
    setForm((previous) =>
      previous.results === results ? previous : { ...previous, results }
    );
  }, []);

  // Stable per-file-field handler keyed by field name.
  const handleFileChange = useCallback((field, file) => {
    setFiles((previous) => {
      if (previous[field] === file) return previous;
      return { ...previous, [field]: file };
    });
  }, []);

  // Memoize the per-field handlers so ImageUploadField doesn't re-render.
  const fileHandlers = useMemo(
    () =>
      FILE_FIELDS.reduce((acc, field) => {
        acc[field.name] = (file) => handleFileChange(field.name, file);
        return acc;
      }, {}),
    [handleFileChange]
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      onSubmit({ form, files, gallery });
    },
    [form, files, gallery, onSubmit]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl bg-gradient-to-r from-brand-50 to-accent-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-brand-700 shadow-sm">
            <PanelsTopLeft size={20} />
          </div>

          <div>
            <h3 className="font-semibold text-slate-900">
              {editing ? "Update project details" : "New portfolio project"}
            </h3>
            <p className="mt-1 text-sm leading-5 text-slate-600">
              Add project information, images, technologies, features,
              results, and client feedback.
            </p>
          </div>
        </div>
      </div>

      {/* Basic info */}
      <section className={sectionClass}>
        <SectionHeading
          icon={FileText}
          title="Basic information"
          description="The main information displayed in your portfolio."
          number="01"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Project title" required>
            <input
              className={inputClass}
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g. PIXELMINDSOLUTIONS"
              required
              maxLength={150}
            />
          </Field>

          <Field label="Project type" required>
            <div className="relative">
              <select
                className={`${inputClass} appearance-none pr-10`}
                name="type"
                value={form.type}
                onChange={handleChange}
                required
              >
                <option value="website">Website</option>
                <option value="mobile-app">Mobile app</option>
                <option value="web-app">Web app</option>
                <option value="digital-marketing">Others</option>
              </select>

              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>
          </Field>

          <Field
            label="Short description"
            required
            hint="A concise summary displayed on project cards."
            className="sm:col-span-2"
          >
            <textarea
              className={`${inputClass} min-h-24 resize-y`}
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the project..."
              required
              maxLength={2000}
            />
          </Field>

          <Field
            label="Full description"
            hint="Detailed project information for the detail page."
            className="sm:col-span-2"
          >
            <textarea
              className={`${inputClass} min-h-32 resize-y`}
              name="fullDescription"
              value={form.fullDescription}
              onChange={handleChange}
              placeholder="Explain the project goals, solution, implementation and impact..."
            />
          </Field>
        </div>
      </section>

      {/* Metadata */}
      <section className={sectionClass}>
        <SectionHeading
          icon={Building2}
          title="Project details"
          description="Client, category, timeline, and website."
          number="02"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Client name">
            <div className="relative">
              <Building2
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                className={`${inputClass} pl-10`}
                name="client"
                value={form.client}
                onChange={handleChange}
                placeholder="PixelMind Solutions"
              />
            </div>
          </Field>

          <Field label="Category">
            <input
              className={inputClass}
              name="category"
              value={form.category}
              onChange={handleChange}
              placeholder="Web Development"
            />
          </Field>

          <Field label="Project year">
            <div className="relative">
              <CalendarDays
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                className={`${inputClass} pl-10`}
                name="year"
                value={form.year}
                onChange={handleChange}
                placeholder="2026"
              />
            </div>
          </Field>

          <Field label="Project duration">
            <div className="relative">
              <Clock3
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                className={`${inputClass} pl-10`}
                name="duration"
                value={form.duration}
                onChange={handleChange}
                placeholder="4 Months"
              />
            </div>
          </Field>

          <Field
            label="Website URL"
            hint="Include https:// or http://."
            className="sm:col-span-2"
          >
            <div className="relative">
              <Globe
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                className={`${inputClass} pl-10`}
                name="websiteLink"
                type="url"
                value={form.websiteLink}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>
          </Field>
        </div>
      </section>

      {/* Images */}
      <section className={sectionClass}>
        <SectionHeading
          icon={ImagePlus}
          title="Project images"
          description="Main image, logo, background image, and gallery."
          number="03"
        />

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {FILE_FIELDS.map((field) => (
            <ImageUploadField
              key={field.name}
              {...field}
              selectedFile={files[field.name] || null}
              existingImage={editing?.[field.name]}
              onChange={fileHandlers[field.name]}
            />
          ))}
        </div>

        <div className="mt-4">
          <GalleryUpload
            gallery={gallery}
            setGallery={setGallery}
            existingGallery={editing?.gallery || []}
          />
        </div>
      </section>

      {/* Technologies */}
      <section className={sectionClass}>
        <SectionHeading
          icon={Code2}
          title="Technologies"
          description="Add the tools and technologies used in this project."
          number="04"
        />

        <ArrayInput
          label="Technology stack"
          description="Press Enter or click Add to add each technology."
          icon={Code2}
          values={form.technologies}
          onChange={setTechnologies}
          placeholder="React, Node.js, MongoDB..."
        />
      </section>

      {/* Features */}
      <section className={sectionClass}>
        <SectionHeading
          icon={ListChecks}
          title="Project features"
          description="List the main capabilities delivered."
          number="05"
        />

        <ArrayInput
          label="Features"
          description="Add each feature individually."
          icon={Check}
          values={form.features}
          onChange={setFeatures}
          placeholder="Responsive Design..."
        />
      </section>

      {/* Results */}
      <section className={sectionClass}>
        <SectionHeading
          icon={ChartNoAxesCombined}
          title="Project results"
          description="Add performance metrics as key-value pairs."
          number="06"
        />

        <ResultsEditor rows={form.results} onChange={setResults} />
      </section>

      {/* Testimonial */}
      <section className={sectionClass}>
        <SectionHeading
          icon={MessageSquareQuote}
          title="Client testimonial"
          description="Client feedback displayed with the project."
          number="07"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Client name">
            <input
              className={inputClass}
              name="name"
              value={form.testimonial.name}
              onChange={handleTestimonialChange}
              placeholder="John Doe"
            />
          </Field>

          <Field label="Client position">
            <input
              className={inputClass}
              name="position"
              value={form.testimonial.position}
              onChange={handleTestimonialChange}
              placeholder="CEO, PixelMind Solutions"
            />
          </Field>

          <Field
            label="Testimonial text"
            hint="The client's review or feedback."
            className="sm:col-span-2"
          >
            <textarea
              className={`${inputClass} min-h-28 resize-y`}
              name="text"
              value={form.testimonial.text}
              onChange={handleTestimonialChange}
              placeholder="Write the client's feedback..."
              maxLength={3000}
            />
          </Field>
        </div>

        {(form.testimonial.name ||
          form.testimonial.position ||
          form.testimonial.text) && (
          <div className="mt-4 rounded-lg border border-brand-100 bg-brand-50/60 p-4">
            <p className="mb-2 text-xs font-semibold text-brand-700">
              Testimonial preview
            </p>
            {form.testimonial.text && (
              <p className="text-sm italic leading-6 text-slate-700">
                “{form.testimonial.text}”
              </p>
            )}
            <div className="mt-3">
              <p className="text-sm font-semibold text-slate-900">
                {form.testimonial.name || "Client name"}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {form.testimonial.position || "Client position"}
              </p>
            </div>
          </div>
        )}
      </section>

      <div className="sticky bottom-0 z-10 -mx-1 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white/95 px-1 pb-1 pt-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">
          <span className="text-red-500">*</span> Required fields
        </p>

        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <button
            type="button"
            className="btn-secondary justify-center"
            disabled={saving}
            onClick={onCancel}
          >
            Cancel
          </button>

          <button
            className="btn-primary justify-center"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Saving project...
              </>
            ) : (
              <>
                <Check size={16} />
                {editing ? "Save changes" : "Create project"}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
});

/* =========================================================
   MAIN PAGE
========================================================= */

export default function Portfolios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [viewModal, setViewModal] = useState(false);
  const [viewProject, setViewProject] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  // Keep the latest editing value in a ref so PortfolioForm's
  // `key` prop forces a clean remount when switching projects.
  const formKey = editing?._id || "create";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/api/portfolios");
      setItems(unwrapList(response));
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setError("");
    setSuccess("");
    setModal(true);
  }, []);

  const openEdit = useCallback((item) => {
    setEditing(item);
    setError("");
    setSuccess("");
    setModal(true);
  }, []);

  const closeModal = useCallback(() => {
    if (!saving) setModal(false);
  }, [saving]);

  const openView = useCallback(async (item) => {
    setViewModal(true);
    setViewProject(null);
    setViewError("");
    setViewLoading(true);

    try {
      const response = await api.get(`/api/portfolios/${item._id}`);
      setViewProject(response.data?.data || response.data);
    } catch (requestError) {
      setViewError(getErrorMessage(requestError));
    } finally {
      setViewLoading(false);
    }
  }, []);

  // Receive the complete payload from PortfolioForm.
  const handleFormSubmit = useCallback(
    async ({ form, files, gallery }) => {
      setError("");
      setSuccess("");

      if (!form.title.trim()) {
        setError("Project title is required.");
        return;
      }
      if (!form.description.trim()) {
        setError("Short description is required.");
        return;
      }
      if (!editing && !files.image) {
        setError("Please select a main project image.");
        return;
      }
      if (
        form.websiteLink.trim() &&
        !/^https?:\/\//i.test(form.websiteLink.trim())
      ) {
        setError("Website URL must begin with https:// or http://.");
        return;
      }

      setSaving(true);

      try {
        const formData = new FormData();

        const textFields = [
          "title",
          "type",
          "description",
          "fullDescription",
          "websiteLink",
          "client",
          "category",
          "year",
          "duration",
        ];

        textFields.forEach((field) => {
          formData.append(field, form[field] ?? "");
        });

        formData.append(
          "technologies",
          JSON.stringify(form.technologies)
        );
        formData.append("features", JSON.stringify(form.features));
        formData.append(
          "results",
          JSON.stringify(resultsToObject(form.results))
        );
        formData.append(
          "testimonial",
          JSON.stringify(form.testimonial)
        );

        Object.entries(files).forEach(([field, file]) => {
          if (file) formData.append(field, file);
        });

        gallery.forEach((file) => {
          formData.append("gallery", file);
        });

        if (editing) {
          await api.put(`/api/portfolios/${editing._id}`, formData);
        } else {
          await api.post("/api/portfolios", formData);
        }

        setSuccess(
          editing
            ? "Portfolio project updated successfully."
            : "Portfolio project created successfully."
        );

        setModal(false);
        setEditing(null);
        await load();
      } catch (requestError) {
        setError(getErrorMessage(requestError));
      } finally {
        setSaving(false);
      }
    },
    [editing, load]
  );

  const remove = useCallback(async () => {
    if (!deleteTarget) return;
    setError("");

    try {
      await api.delete(`/api/portfolios/${deleteTarget._id}`);
      setItems((previous) =>
        previous.filter((item) => item._id !== deleteTarget._id)
      );
      setSuccess("Portfolio project deleted successfully.");
      setDeleteTarget(null);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  // Memoize derived data so it doesn't recompute on unrelated renders.
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return items;

    return items.filter((item) =>
      [item.title, item.type, item.client, item.category, item.description]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [items, query]);

  const typeCounts = useMemo(() => {
    return items.reduce((counts, item) => {
      counts[item.type] = (counts[item.type] || 0) + 1;
      return counts;
    }, {});
  }, [items]);

  const handleQueryChange = useCallback((event) => {
    setQuery(event.target.value);
  }, []);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Portfolio projects"
        description="Showcase your work and manage every project detail."
      >
        <button type="button" onClick={openCreate} className="btn-primary">
          <Plus size={17} />
          Add project
        </button>
      </PageHeader>

      <SuccessMessage>{success}</SuccessMessage>
      <ErrorMessage>{error}</ErrorMessage>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          {
            label: "Total projects",
            value: items.length,
            icon: PanelsTopLeft,
            color: "bg-brand-50 text-brand-700",
          },
          {
            label: "Websites",
            value: typeCounts.website || 0,
            icon: Globe,
            color: "bg-accent-50 text-accent-700",
          },
          {
            label: "Web apps",
            value: typeCounts["web-app"] || 0,
            icon: Code2,
            color: "bg-slate-100 text-slate-700",
          },
          {
            label: "Mobile apps",
            value: typeCounts["mobile-app"] || 0,
            icon: Layers3,
            color: "bg-emerald-50 text-emerald-600",
          },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-slate-500 sm:text-sm">
                  {stat.label}
                </p>
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${stat.color}`}
                >
                  <Icon size={18} />
                </div>
              </div>
              <p className="mt-3 text-2xl font-semibold text-slate-900">
                {stat.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Search + table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="relative w-full sm:max-w-md">
            <Search
              size={17}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              className={`${inputClass} pl-10`}
              placeholder="Search title, type, client or category..."
              value={query}
              onChange={handleQueryChange}
            />
          </div>

          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="btn-secondary"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {loading ? (
          <Loading label="Loading portfolio projects..." />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={
              query ? "No matching projects" : "No portfolio projects yet"
            }
            detail={
              query
                ? "Try another search term."
                : "Add your first project to get started."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr>
                  <th className="table-head px-5 py-3">Project</th>
                  <th className="table-head px-5 py-3">Type</th>
                  <th className="table-head px-5 py-3">Client / category</th>
                  <th className="table-head px-5 py-3">Year</th>
                  <th className="table-head px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((item) => (
                  <tr
                    key={item._id}
                    className="transition hover:bg-slate-50/70"
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                          {item.image ? (
                            <img
                              src={imageUrl(item.image)}
                              alt={item.title || "Project"}
                              className="h-full w-full object-cover"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <PanelsTopLeft
                              size={20}
                              className="text-slate-500"
                            />
                          )}
                        </div>

                        <div className="max-w-[280px]">
                          <p className="truncate font-semibold text-slate-900">
                            {item.title}
                          </p>
                          <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="table-cell">
                      <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                        {item.type}
                      </span>
                    </td>

                    <td className="table-cell">
                      <p className="font-medium text-slate-700">
                        {item.client || "—"}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {item.category || "Uncategorized"}
                      </p>
                    </td>

                    <td className="table-cell">{item.year || "—"}</td>

                    <td className="table-cell">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openView(item)}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-accent-100 hover:bg-accent-50 hover:text-accent-700"
                          title="View project"
                          aria-label={`View ${item.title}`}
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-800"
                          title="Edit project"
                          aria-label={`Edit ${item.title}`}
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                          title="Delete project"
                          aria-label={`Delete ${item.title}`}
                        >
                          <Trash2 size={16} />
                        </button>

                        {item.websiteLink && (
                          <a
                            href={item.websiteLink}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50"
                            title="Open website"
                            aria-label={`Open ${item.title} website`}
                          >
                            <ExternalLink size={16} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
              Showing {filtered.length} of {items.length} projects
            </div>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      <Modal
        open={modal}
        onClose={closeModal}
        title={
          editing ? "Edit portfolio project" : "Create portfolio project"
        }
        wide
      >
        {/*
          The `key` forces a fresh PortfolioForm instance whenever the
          edit target changes, so state does not leak between projects.
        */}
        <PortfolioForm
          key={formKey}
          editing={editing}
          saving={saving}
          onCancel={closeModal}
          onSubmit={handleFormSubmit}
        />
      </Modal>

      {/* VIEW MODAL */}
      <Modal
        open={viewModal}
        onClose={() => setViewModal(false)}
        title="Portfolio project details"
        wide
      >
        <div className="max-h-[75vh] overflow-y-auto pr-1">
          {viewLoading ? (
            <Loading label="Loading complete project details..." />
          ) : viewError ? (
            <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              {viewError}
            </div>
          ) : viewProject ? (
            <PortfolioView project={viewProject} />
          ) : (
            <p className="py-8 text-center text-sm text-slate-500">
              No project details available.
            </p>
          )}
        </div>

        <div className="mt-5 flex flex-col-reverse justify-end gap-2 border-t border-slate-100 pt-4 sm:flex-row">
          {viewProject?.websiteLink && (
            <a
              href={viewProject.websiteLink}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary justify-center"
            >
              <ExternalLink size={15} />
              Visit website
            </a>
          )}

          {viewProject && (
            <button
              type="button"
              onClick={() => {
                const project = viewProject;
                setViewModal(false);
                openEdit(project);
              }}
              className="btn-primary justify-center"
            >
              <Pencil size={15} />
              Edit project
            </button>
          )}

          <button
            type="button"
            onClick={() => setViewModal(false)}
            className="btn-secondary justify-center"
          >
            Close
          </button>
        </div>
      </Modal>

      {/* DELETE MODAL */}
      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete portfolio project"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4">
            <AlertCircle
              size={21}
              className="mt-0.5 shrink-0 text-red-600"
            />
            <p className="text-sm leading-6 text-red-800">
              Are you sure you want to delete{" "}
              <strong>{deleteTarget?.title}</strong>? This action cannot be
              undone.
            </p>
          </div>

          <p className="text-sm leading-6 text-slate-500">
            The backend may also delete the uploaded images associated with
            this project.
          </p>

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700"
              onClick={remove}
            >
              <Trash2 size={16} />
              Delete project
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}